import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { lazy, Suspense, useMemo, type ReactElement } from "react";
import { ContentHeader, KpiCard } from "@cx/ui";
import type { AbcResult } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { useSession } from "../../session";
import { chartOptions, unitFormatter, type ChartUnit } from "../dashboard/chart";
import { count, money, pct, periodLabel, qty, reportQuery } from "../dashboard/labels";
import { FilterRow, ReportStatus, StatusFilter, reportQueryOptions } from "../dashboard/shared";
import { DateField, ExportButtons, HistoryStatus, MultiFilter, listOf, useAnalyticsStatus } from "./shared";

const PATH = "/meu-negocio/curva-abc";
const Chart = lazy(() => import("react-apexcharts"));
/** No gráfico de Pareto, só os primeiros produtos (a tabela tem todos). */
const CHART_ROWS = 30;
const TABLE_ROWS = 500;

const METRIC_LABEL = { revenue: "Receita", margin: "Margem", qty: "Quantidade" } as const;
const CLASS_TONE = { A: "success", B: "warning", C: "secondary" } as const;

/** Curva ABC (RF-BI-12): receita, margem (com custo) ou quantidade, a partir do histórico no lago. */
export function AbcPage(): ReactElement {
  const { companyId, api, me } = useSession();
  const search = useSearch({ from: PATH });
  const navigate = useNavigate({ from: PATH });
  const status = useAnalyticsStatus();
  const set = (patch: Partial<typeof search>) =>
    navigate({ search: (s) => ({ ...s, ...patch }), replace: true });
  const withCost = can(me.permissions as Permissions, "products", "read_cost");
  const metric = search.metric === "margin" && !withCost ? "revenue" : (search.metric ?? "revenue");
  const [from, to] = search.from && search.to ? [search.from, search.to] : [undefined, undefined];
  const params = { from, to, channels: search.channels, statuses: search.statuses, metric };
  const q = useQuery({
    ...reportQueryOptions,
    queryKey: ["analytics", companyId, "abc", params],
    queryFn: () => api<AbcResult>(`/analytics/abc${reportQuery(params)}`),
  });
  const r = q.data;
  const unit: ChartUnit = metric === "qty" ? "qty" : "money";
  const fmt = metric === "qty" ? qty : money;
  return (
    <>
      <ContentHeader
        title="Curva ABC"
        breadcrumb={["Meu Negócio", "Curva ABC"]}
        actions={
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <HistoryStatus />
            <ExportButtons report="abc" params={params} />
          </div>
        }
      />
      <FilterRow>
        <DateField id="abc-from" label="De" value={search.from} onChange={(v) => set({ from: v })} />
        <DateField id="abc-to" label="Até" value={search.to} onChange={(v) => set({ to: v })} />
        <div className="col-6 col-md-2">
          <label className="form-label small" htmlFor="abc-metric">
            Curva por
          </label>
          <select
            id="abc-metric"
            className="form-select"
            value={metric}
            onChange={(e) => set({ metric: e.target.value as typeof metric })}
          >
            <option value="revenue">Receita</option>
            {withCost && <option value="margin">Margem</option>}
            <option value="qty">Quantidade</option>
          </select>
        </div>
        <div className="col-6 col-md-3">
          {status.data && (
            <MultiFilter
              label="Canais"
              allLabel="Todos os canais"
              options={status.data.channels}
              value={listOf(search.channels)?.map((k) => k)}
              onChange={(v) => set({ channels: v?.join(",") })}
            />
          )}
        </div>
        <div className="col-12 col-md-3">
          {status.data && (
            <StatusFilter
              statuses={status.data.statuses}
              defaults={status.data.saleStatusIds}
              value={listOf(search.statuses)}
              onChange={(ids) => set({ statuses: ids?.join(",") })}
            />
          )}
        </div>
      </FilterRow>
      <ReportStatus loading={q.isLoading} error={q.error} />
      {r && (
        <>
          <p className="text-muted small">
            {periodLabel(r.period)} · {METRIC_LABEL[r.metric]} total: {fmt(r.total)}
            {r.noCostProducts > 0 &&
              ` · ${count(r.noCostProducts)} produto(s) vendido(s) sem custo cadastrado ficam fora da curva de margem`}
          </p>
          <div className="row g-3 mb-3">
            {(["A", "B", "C"] as const).map((k) => (
              <div className="col-12 col-md-4" key={k}>
                <KpiCard
                  label={`Classe ${k} — ${count(r.summary[k].count)} produto(s)`}
                  value={fmt(r.summary[k].value)}
                  hint={`${pct(r.summary[k].sharePct4)} do total`}
                  testId={`abc-class-${k}`}
                />
              </div>
            ))}
          </div>
          {r.rows.length === 0 ? (
            <div className="box card p-3 text-muted mb-3">
              Sem vendas no período. Se acabou de vender, use “Atualizar agora”.
            </div>
          ) : (
            <>
              <Pareto rows={r.rows.slice(0, CHART_ROWS)} unit={unit} metricLabel={METRIC_LABEL[r.metric]} />
              <div className="box card mt-3">
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0" aria-label="Curva ABC: produtos">
                    <thead>
                      <tr>
                        <th scope="col">Classe</th>
                        <th scope="col">Produto</th>
                        <th scope="col">SKU</th>
                        <th scope="col" className="text-end">
                          {METRIC_LABEL[r.metric]}
                        </th>
                        <th scope="col" className="text-end">
                          Participação
                        </th>
                        <th scope="col" className="text-end">
                          Acumulado
                        </th>
                        <th scope="col" className="text-end">
                          Pedidos
                        </th>
                        {withCost && r.metric !== "margin" && (
                          <th scope="col" className="text-end">
                            Margem
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {r.rows.slice(0, TABLE_ROWS).map((x) => (
                        <tr key={x.productId}>
                          <td>
                            <span className={`badge text-bg-${CLASS_TONE[x.cls]}`}>{x.cls}</span>
                          </td>
                          <td>{x.name}</td>
                          <td className="text-muted">{x.sku ?? "—"}</td>
                          <td className="text-end">{fmt(x.value)}</td>
                          <td className="text-end">{pct(x.sharePct4)}</td>
                          <td className="text-end">{pct(x.cumulativePct4)}</td>
                          <td className="text-end">{count(x.orders)}</td>
                          {withCost && r.metric !== "margin" && (
                            <td className="text-end">{money(x.marginCents ?? null)}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(r.rows.length > TABLE_ROWS || r.truncated) && (
                  <p className="small text-muted p-2 mb-0">
                    Mostrando os {TABLE_ROWS} primeiros produtos. Exporte para ver todos.
                  </p>
                )}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

/** Pareto: barras do valor por produto e a linha da participação acumulada (eixo da direita, 0–100%). */
function Pareto({
  rows,
  unit,
  metricLabel,
}: {
  rows: AbcResult["rows"];
  unit: ChartUnit;
  metricLabel: string;
}): ReactElement {
  const categories = useMemo(() => rows.map((r) => r.sku ?? r.name), [rows]);
  const options = useMemo(() => {
    const base = chartOptions({ type: "bar", categories, unit, filename: "curva-abc" });
    const fmt = unitFormatter(unit);
    return {
      ...base,
      stroke: { width: [0, 2], curve: "straight" as const },
      yaxis: [
        { labels: { formatter: (v: number) => fmt(v) } },
        { opposite: true, min: 0, max: 100, labels: { formatter: (v: number) => `${Math.round(v)}%` } },
      ],
      tooltip: {
        ...base.tooltip,
        y: {
          formatter: (v: number, o?: { seriesIndex?: number }) =>
            o?.seriesIndex === 1 ? `${v.toLocaleString("pt-BR")}%` : fmt(v),
        },
      },
    };
  }, [categories, unit]);
  const series = [
    { name: metricLabel, type: "column", data: rows.map((r) => r.value) },
    { name: "Acumulado (%)", type: "line", data: rows.map((r) => r.cumulativePct4 / 10_000) },
  ];
  return (
    <section className="box card" aria-label="Gráfico de Pareto" data-testid="abc-pareto">
      <div className="card-header">
        <h2 className="h6 mb-0">Pareto ({rows.length} primeiros)</h2>
      </div>
      <div className="card-body">
        <Suspense fallback={<p className="text-muted mb-0">Carregando gráfico…</p>}>
          <div aria-hidden="true">
            <Chart type="line" series={series} options={options} height={320} />
          </div>
        </Suspense>
      </div>
    </section>
  );
}
